import type { Position } from './warenkorb';

/**
 * Rechnung teilen (Gastro: getrennt zahlen): Aus dem Bon wird ein Teil jetzt
 * kassiert (eigener Beleg), der Rest bleibt als Bon stehen. Die Auswahl nennt je
 * Position (Index) die Menge, die jetzt bezahlt wird; der Zeilenbetrag wird
 * anteilig in Cent aufgeteilt, Rundungsreste bleiben beim Rest (Regel 5).
 * Rabattzeilen (negativ) und Gutscheine werden nicht geteilt – vor dem Teilen entfernen.
 */
export type Teilauswahl = ReadonlyMap<number, number>;

export interface Teilung {
  jetzt: Position[];
  rest: Position[];
}

/** Anteiliger Betrag für `menge` von `p.menge` Einheiten, kaufmännisch gerundet. */
export function anteilCent(p: Position, menge: number): number {
  if (menge >= p.menge) {
    return p.bruttoCent;
  }
  return Math.round((p.bruttoCent * menge) / p.menge);
}

export function teileBon(positionen: readonly Position[], auswahl: Teilauswahl): Teilung {
  const jetzt: Position[] = [];
  const rest: Position[] = [];
  positionen.forEach((p, i) => {
    const n = Math.max(0, Math.min(p.menge, Math.floor(auswahl.get(i) ?? 0)));
    if (n <= 0) {
      rest.push(p);
      return;
    }
    const cent = anteilCent(p, n);
    jetzt.push({ ...p, menge: n, bruttoCent: cent });
    if (n < p.menge) {
      rest.push({ ...p, menge: p.menge - n, bruttoCent: p.bruttoCent - cent });
    }
  });
  return { jetzt, rest };
}

/** Auswahl anpassen: Menge je Position zwischen 0 und p.menge halten. */
export function auswahlSetzen(
  auswahl: Teilauswahl,
  positionen: readonly Position[],
  index: number,
  menge: number,
): Map<number, number> {
  const neu = new Map(auswahl);
  const p = positionen[index];
  if (p === undefined) {
    return neu;
  }
  const n = Math.max(0, Math.min(p.menge, Math.floor(menge)));
  if (n === 0) {
    neu.delete(index);
  } else {
    neu.set(index, n);
  }
  return neu;
}

/** Darf der Bon geteilt werden? Nur ohne Rabatt-/Negativzeilen und mit mehr als einer Einheit. */
export function teilbar(positionen: readonly Position[]): boolean {
  if (positionen.some((p) => p.bruttoCent < 0 || p.menge <= 0)) {
    return false;
  }
  return positionen.reduce((s, p) => s + p.menge, 0) > 1;
}

/**
 * Restbon nach einem Beleg: zieht die bezahlten Positionen (Menge und Betrag) vom Bon
 * ab. Gleich nach Bezeichnung, Steuersatz und Artikel – unabhängig von der
 * Teilen-Auswahl, die nach einem Neuladen fehlt. Was nicht (mehr) im Bon steht,
 * wird ignoriert; vollständig bezahlte Zeilen verschwinden.
 */
export function restNachZahlung(
  bon: readonly Position[],
  bezahlt: readonly Position[],
): Position[] {
  const rest = bon.map((p) => ({ ...p }));
  for (const b of bezahlt) {
    const i = rest.findIndex(
      (p) =>
        p.menge > 0 &&
        p.bezeichnung === b.bezeichnung &&
        p.steuersatz === b.steuersatz &&
        p.artikelId === b.artikelId &&
        Math.sign(p.bruttoCent) === Math.sign(b.bruttoCent),
    );
    const p = rest[i];
    if (p === undefined) {
      continue;
    }
    const menge = Math.min(p.menge, b.menge);
    // Teilmenge: genau der bezahlte Betrag (Rundungsrest bleibt wie beim Teilen beim Rest).
    const cent = menge === p.menge ? p.bruttoCent : b.bruttoCent;
    rest[i] = { ...p, menge: p.menge - menge, bruttoCent: p.bruttoCent - cent };
  }
  return rest.filter((p) => p.menge > 0);
}
